import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import logger from "../../logger";
import {
  Button,
  Card,
  Text,
  MessageBar,
  MessageBarBody,
  Spinner,
  Image,
  Dropdown,
  Option,
} from "@fluentui/react-components";
import { useAuth } from "./AuthContext";
import { useLoginStyles } from "./Login.styles";
import { saveSelectedEnvironment } from "../services/environmentStorage";

const isProduction = process.env.NODE_ENV === "production";

const Login = () => {
  const { isAuthenticated, login, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedEnvironment, setSelectedEnvironment] = useState(
    isProduction ? "production" : null
  );
  const styles = useLoginStyles();

  useEffect(() => {
    if (isAuthenticated) {
      const savedRoute = sessionStorage.getItem("lastRoute");
      navigate(savedRoute || "/organization-list", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleLogin = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (!selectedEnvironment) {
      setError("Please select an environment before signing in.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await saveSelectedEnvironment(selectedEnvironment);
      await login(selectedEnvironment);
    } catch (error) {
      logger.error("Login error:", error);
      setError(error.message || "Failed to sign in. Please try again.");
      setLoading(false);
    }
  };

  const handleEnvironmentChange = (event, data) => {
    setSelectedEnvironment(data.optionValue);
    setError(null);
  };

  const getEnvironmentDisplayValue = () => {
    if (!selectedEnvironment) return "";
    return selectedEnvironment.charAt(0).toUpperCase() + selectedEnvironment.slice(1);
  };

  return (
    <div className={styles.container}>
      <Card className={styles.card}>
        <div className={styles.hero}>
          <div className={styles.heroLogo}>
            <Image src="../../../../assets/logo.png" alt="Novata" />
          </div>
          <Text as="h1" className={styles.heroTitle}>
            Welcome to Novata.
          </Text>
          <div className={styles.heroAccent} aria-hidden="true">
            <span className={styles.heroDotLine} style={{ width: "24%", left: "8%" }} />
            <span className={styles.heroDotLine} style={{ width: "40%", left: "20%" }} />
            <span className={styles.heroDotLine} style={{ width: "30%", left: "12%" }} />
            <span className={styles.heroDotLine} style={{ width: "44%", left: "32%" }} />
            <span className={styles.heroDotLine} style={{ width: "20%", left: "6%" }} />
          </div>
        </div>

        <div className={styles.content}>
          <Text as="h2" className={styles.title}>
            Sign In
          </Text>

          {!isProduction && (
            <div className={styles.dropdownContainer}>
              <Text as="label" className={styles.dropdownLabel}>
                Environment
              </Text>
              <Dropdown
                placeholder="Select environment"
                className={styles.dropdown}
                value={getEnvironmentDisplayValue()}
                onOptionSelect={handleEnvironmentChange}
                disabled={loading || authLoading}
              >
                <Option value="local">Local</Option>
                <Option value="staging">Staging</Option>
                <Option value="production">Production</Option>
              </Dropdown>
            </div>
          )}

          <Button
            size="large"
            className={styles.button}
            onClick={handleLogin}
            disabled={loading || authLoading || !selectedEnvironment}
          >
            {loading || authLoading ? "Signing in…" : "Sign in"}
          </Button>

          <a
            href="https://knowledge.novata.com/knowledge/help-with-my-novata-account"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.helpLink}
          >
            Help
          </a>

          {error && (
            <MessageBar intent="error" className={styles.errorText}>
              <MessageBarBody>
                {error}
                {error.includes("Popup blocked") && (
                  <div className={styles.helpText}>
                    <Text weight="semibold">How to fix:</Text>
                    <ul className={styles.helpList}>
                      <li>Check your browser's popup blocker settings</li>
                      <li>Allow popups for this site</li>
                      <li>Try clicking the button again after allowing popups</li>
                    </ul>
                  </div>
                )}
              </MessageBarBody>
            </MessageBar>
          )}

          {(loading || authLoading) && !error && (
            <div className={styles.loadingText}>
              <Spinner size="tiny" />
              <Text>Opening sign-in window…</Text>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default Login;
